import { ref, set, get, remove, onValue } from 'firebase/database';
import { db } from '../services/firebase';
import { useState, useEffect, useCallback } from 'react';

export interface FriendProfile {
  uid: string;
  displayName?: string;
  avatar?: string;
  stats?: { won: number; played: number };
  addedAt?: number;
  sentAt?: number;
}

export function useFriends(uid: string | undefined) {
  const [friends, setFriends] = useState<FriendProfile[]>([]);
  const [incoming, setIncoming] = useState<FriendProfile[]>([]);
  const [outgoing, setOutgoing] = useState<string[]>([]);

  // Listen to friends list real-time
  useEffect(() => {
    if (!uid) return;

    const friendsRef = ref(db, `friends/${uid}/friends`);
    const incomingRef = ref(db, `friends/${uid}/incoming`);
    const outgoingRef = ref(db, `friends/${uid}/outgoing`);

    const unsubFriends = onValue(friendsRef, async (snap) => {
      const data = snap.val() || {};
      const friendIds = Object.keys(data);
      if (friendIds.length === 0) {
        setFriends([]);
        return;
      }
      // Fetch friend profiles
      const profiles = await Promise.all(
        friendIds.map(async (fUid) => {
          const s = await get(ref(db, `users/${fUid}`));
          return s.exists() ? { uid: fUid, ...s.val(), ...data[fUid] } : null;
        })
      );
      setFriends(profiles.filter(Boolean) as FriendProfile[]);
    });

    const unsubIncoming = onValue(incomingRef, async (snap) => {
      const data = snap.val() || {};
      const fromIds = Object.keys(data);
      if (fromIds.length === 0) {
        setIncoming([]);
        return;
      }
      const profiles = await Promise.all(
        fromIds.map(async (fUid) => {
          const s = await get(ref(db, `users/${fUid}`));
          return s.exists() ? { uid: fUid, ...s.val(), ...data[fUid] } : null;
        })
      );
      setIncoming(profiles.filter(Boolean) as FriendProfile[]);
    });

    const unsubOutgoing = onValue(outgoingRef, (snap) => {
      const data = snap.val() || {};
      setOutgoing(Object.keys(data));
    });

    return () => {
      unsubFriends();
      unsubIncoming();
      unsubOutgoing();
    };
  }, [uid]);

  const sendFriendRequest = useCallback(async (toUid: string) => {
    if (!uid || toUid === uid) return;
    try {
      await set(ref(db, `friends/${uid}/outgoing/${toUid}`), { sentAt: Date.now() });
      await set(ref(db, `friends/${toUid}/incoming/${uid}`), { addedAt: Date.now() });
    } catch (err) {
      console.error('sendFriendRequest failed:', err);
      throw err;
    }
  }, [uid]);

  const acceptFriendRequest = useCallback(async (fromUid: string) => {
    if (!uid) return;
    // Remove from incoming
    await remove(ref(db, `friends/${uid}/incoming/${fromUid}`));
    // Add to both friends lists
    await set(ref(db, `friends/${uid}/friends/${fromUid}`), { addedAt: Date.now(), status: 'accepted' });
    await set(ref(db, `friends/${fromUid}/friends/${uid}`), { addedAt: Date.now(), status: 'accepted' });
  }, [uid]);

  const declineFriendRequest = useCallback(async (fromUid: string) => {
    if (!uid) return;
    await remove(ref(db, `friends/${uid}/incoming/${fromUid}`));
  }, [uid]);

  const removeFriend = useCallback(async (friendUid: string) => {
    if (!uid) return;
    await remove(ref(db, `friends/${uid}/friends/${friendUid}`));
    await remove(ref(db, `friends/${friendUid}/friends/${uid}`));
  }, [uid]);

  const searchUsers = useCallback(async (query: string) => {
    // Simple prefix search on displayName — Firebase doesn't support full-text
    // Fetch all users and filter client-side (small dataset assumed)
    if (!query || query.length < 2) return [];
    const usersRef = ref(db, 'users');
    const snap = await get(usersRef);
    if (!snap.exists()) return [];
    const all = snap.val();
    const results = Object.entries(all)
      .filter(([id, data]: [string, any]) =>
        id !== uid && data.displayName?.toLowerCase().includes(query.toLowerCase())
      )
      .slice(0, 10)
      .map(([id, data]: [string, any]) => ({ uid: id, ...(data as object) }));
    return results;
  }, [uid]);

  return { friends, incoming, outgoing, sendFriendRequest, acceptFriendRequest, declineFriendRequest, removeFriend, searchUsers };
}